import {
  Badge,
  Button,
  Card,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text
} from '@mantine/core'
import {
  IconCircleCheckFilled,
  IconPlayerPlay,
  IconPlayerStop,
  IconPlus,
  IconRefresh,
  IconTrash
} from '@tabler/icons-react'
import { useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useInvalidateDeviceList } from '@/queries/deviceQueries'
import {
  Device,
  TrackingAction,
  TrackingServiceStatus,
  TrackingStatus
} from '@/types/device'
import { getTrackingStatus, isJetsonDevice } from '@/utils/device/getDeviceAttr'
import { formatSize } from '@/utils/file'
import { isUpdateAvailable, normalizeVersion } from '@/utils/version'
import { DeviceTypeBadge } from '../../devices/DeviceTypeBadge/DeviceTypeBadge'
import { OnlineStatus } from '../../devices/OnlineStatus/OnlineStatus'
import { CrossCameraServiceActionModal } from '../CrossCameraServiceActionModal/CrossCameraServiceActionModal'
import { CrossCameraServiceStatusBadge } from '../CrossCameraServiceStatusBadge/CrossCameraServiceStatusBadge'
import {
  DEFAULT_TRACKING_STATUS,
  OPTIMISTIC_STATUS_TIMEOUT_MS,
  isTrackingInProgress,
  optimisticStatusForAction,
  shouldClearOptimisticStatus
} from '../trackingStatus'

// ThingsBoard attributes update after the RPC; wait before refetching the list.
const DEVICE_STATUS_REFETCH_DELAY_MS = 1000

type CrossCameraDeviceCardProps = {
  device: Device
  canManage?: boolean
  latestVersion?: string
}

type Fact = { labelId: string; value: string }

type OptimisticStatus = {
  status: TrackingServiceStatus
  reportedWhenSet: string
}

const trackingFacts = (tracking?: TrackingStatus): Fact[] => [
  {
    labelId: 'crossCamera.installation.fields.model',
    // The device reports the model with the revision it downloaded appended.
    value: tracking?.model_id?.split(':')[0] || '—'
  },
  {
    labelId: 'crossCamera.installation.fields.pipelines',
    value: String(tracking?.applications ?? 0)
  },
  {
    labelId: 'crossCamera.installation.fields.database',
    value: formatSize(tracking?.database_bytes ?? 0, 'b')
  }
]

export const CrossCameraDeviceCard = ({
  device,
  canManage = true,
  latestVersion
}: CrossCameraDeviceCardProps) => {
  const [pendingAction, setPendingAction] = useState<TrackingAction | null>(
    null
  )
  const [optimistic, setOptimistic] = useState<OptimisticStatus | null>(null)
  const { invalidateDeviceList } = useInvalidateDeviceList()
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Unlike Tower, not a gate: the tracking image also runs on arm64.
  const isJetson = isJetsonDevice(device)
  const tracking = getTrackingStatus(device)
  const reportedStatus = tracking?.service_status ?? DEFAULT_TRACKING_STATUS
  const status = optimistic?.status ?? reportedStatus
  const isBusy = isTrackingInProgress(status)
  const isInstalled = status !== 'not_installed'

  const currentVersion = normalizeVersion(tracking?.version)
  const publishedVersion = normalizeVersion(latestVersion)
  const hasUpdate = isUpdateAvailable(currentVersion, publishedVersion)

  const showActions = canManage && device.active && !isBusy
  const showInstall = status === 'not_installed' || status === 'failed'
  const showStart = status === 'stopped'
  const showStop = status === 'running'
  const showRestart = status === 'running'
  const showUpdate = showActions && hasUpdate && !showInstall
  const showFacts = device.active && isInstalled && status !== 'failed'

  useEffect(() => {
    return () => {
      if (refetchTimerRef.current !== null) {
        clearTimeout(refetchTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (
      optimistic &&
      shouldClearOptimisticStatus(reportedStatus, optimistic.reportedWhenSet)
    ) {
      setOptimistic(null)
    }
  }, [optimistic, reportedStatus])

  useEffect(() => {
    if (!optimistic) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      setOptimistic(null)
    }, OPTIMISTIC_STATUS_TIMEOUT_MS)

    return () => window.clearTimeout(timeoutId)
  }, [optimistic])

  const handleActionClick = (action: TrackingAction) => {
    setPendingAction(action)
  }

  const handleModalClose = () => {
    setPendingAction(null)
  }

  const handleActionSuccess = (action: TrackingAction) => {
    const nextStatus = optimisticStatusForAction(action)

    if (nextStatus) {
      setOptimistic({ status: nextStatus, reportedWhenSet: reportedStatus })
    }

    if (refetchTimerRef.current !== null) {
      clearTimeout(refetchTimerRef.current)
    }
    refetchTimerRef.current = setTimeout(() => {
      refetchTimerRef.current = null
      void invalidateDeviceList()
    }, DEVICE_STATUS_REFETCH_DELAY_MS)
  }

  return (
    <Card withBorder>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Stack gap={4}>
          <Group gap="xs">
            <Text fw={600}>{device.label}</Text>
            <DeviceTypeBadge isJetson={isJetson} />
          </Group>

          <OnlineStatus status={device.active ? 'online' : 'offline'} />

          {showFacts && (
            <SimpleGrid cols={3} spacing="lg" mt="md">
              {trackingFacts(tracking).map((fact) => (
                <Stack key={fact.labelId} gap={2}>
                  <Text size="xs" c="dimmed">
                    <FormattedMessage id={fact.labelId} />
                  </Text>
                  <Text size="sm">{fact.value}</Text>
                </Stack>
              ))}
            </SimpleGrid>
          )}
        </Stack>

        <Paper radius="md" p="md" bg="gray.1">
          <Text fw={600} size="sm" mb="lg">
            <FormattedMessage id="crossCamera.installation.service" />
          </Text>

          <Stack gap="xl">
            <Stack gap={4}>
              {device.active && (
                <CrossCameraServiceStatusBadge status={status} />
              )}

              {device.active && isInstalled && currentVersion && (
                <Group wrap="nowrap" gap="xs" align="center">
                  <Badge size="sm" color="gray">
                    {currentVersion}
                  </Badge>

                  {!hasUpdate && publishedVersion && (
                    <IconCircleCheckFilled
                      size={18}
                      color="var(--mantine-color-green-6)"
                    />
                  )}

                  {showUpdate && (
                    <Button
                      size="compact-xs"
                      variant="outline"
                      onClick={() => handleActionClick('update')}
                    >
                      <FormattedMessage
                        id="crossCamera.installation.installUpdate"
                        values={{ version: publishedVersion }}
                      />
                    </Button>
                  )}
                </Group>
              )}

              {status === 'failed' && tracking?.error && (
                <Text size="xs" c="red">
                  {tracking.error}
                </Text>
              )}
            </Stack>

            {showActions && (
              <Group gap="xs">
                {showInstall && (
                  <Button
                    size="xs"
                    miw={100}
                    leftSection={<IconPlus size={16} />}
                    onClick={() => handleActionClick('install')}
                  >
                    <FormattedMessage id="crossCamera.installation.actions.install" />
                  </Button>
                )}

                {showStart && (
                  <Button
                    size="xs"
                    color="green"
                    miw={100}
                    leftSection={<IconPlayerPlay size={16} />}
                    onClick={() => handleActionClick('start')}
                  >
                    <FormattedMessage id="crossCamera.installation.actions.start" />
                  </Button>
                )}

                {showStop && (
                  <Button
                    size="xs"
                    variant="light"
                    color="red"
                    miw={100}
                    leftSection={<IconPlayerStop size={16} />}
                    onClick={() => handleActionClick('stop')}
                  >
                    <FormattedMessage id="crossCamera.installation.actions.stop" />
                  </Button>
                )}

                {showRestart && (
                  <Button
                    size="xs"
                    variant="light"
                    color="orange"
                    miw={100}
                    leftSection={<IconRefresh size={16} />}
                    onClick={() => handleActionClick('restart')}
                  >
                    <FormattedMessage id="crossCamera.installation.actions.restart" />
                  </Button>
                )}

                {isInstalled && (
                  <Button
                    size="xs"
                    variant="subtle"
                    color="red"
                    leftSection={<IconTrash size={16} />}
                    onClick={() => handleActionClick('uninstall')}
                  >
                    <FormattedMessage id="crossCamera.installation.actions.uninstall" />
                  </Button>
                )}
              </Group>
            )}

            {!device.active && (
              <Text size="xs" c="yellow">
                <FormattedMessage id="crossCamera.installation.deviceOfflineWarning" />
              </Text>
            )}
          </Stack>
        </Paper>
      </SimpleGrid>

      {pendingAction && (
        <CrossCameraServiceActionModal
          action={pendingAction}
          deviceId={device.id}
          deviceName={device.label}
          latestVersion={publishedVersion}
          onClose={handleModalClose}
          onSuccess={handleActionSuccess}
        />
      )}
    </Card>
  )
}
